'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface DeviceSearchInputProps {
  defaultValue?: string;
  placeholder?: string;
  className?: string;
  /** Kept in the URL alongside the query (deleted when empty on home). */
  extraParams?: string[];
}

/**
 * Model search box. The query lives in `?search=` so results are SSR,
 * shareable and indexable; typing navigates (debounced) instead of
 * querying the DB per keystroke — filtering stays static/in-memory.
 */
export function DeviceSearchInput({
  defaultValue = '',
  placeholder = 'ابحث عن موديل… (مثال: Galaxy S25)',
  className,
  extraParams = ['brand', 'layout'],
}: DeviceSearchInputProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [value, setValue] = useState(defaultValue);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  const navigate = (next: string) => {
    const params = new URLSearchParams();
    for (const key of extraParams) {
      const existing = searchParams.get(key);
      if (existing) {
        params.set(key, existing);
      }
    }
    const query = next.trim();
    if (query) {
      params.set('search', query);
    }
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  };

  const handleChange = (next: string) => {
    setValue(next);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => navigate(next), 350);
  };

  return (
    <div className={cn('relative', className)} role="search">
      <Search className="pointer-events-none absolute top-1/2 inset-s-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        value={value}
        onChange={(event) => handleChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-11 rounded-full bg-card ps-10 pe-9 shadow-sm"
      />
      {value ? (
        <button
          type="button"
          onClick={() => handleChange('')}
          aria-label="مسح البحث"
          className="absolute top-1/2 end-3 -translate-y-1/2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}
